/* ============================================================================
   RECOLOR — the PDP Color Compare's tint (ui.js garmentHTML: a colour laid
   over a ghost, masked by the ghost's own alpha), done per pixel so the navy
   renders keep their folds and seams in any uniform colour.

   Every ghost and every avatar shot is navy. To show another colour, each
   garment pixel keeps its shading — its luminance relative to the garment's
   mean — and takes the target colour at that brightness. A lighter target
   flattens the shading (navy's folds are far deeper than a white top's), and
   highlights roll off instead of clipping. Ghosts mask by their own alpha,
   minus the white FIGS label; avatar shots bring a garment mask
   (assets/avatar/*-mask.webp) so skin, hair, socks and sneakers never tint.

   Results are cached per image and colour as object URLs. Navy returns the
   source untouched.
   ========================================================================== */

const toLin = new Float32Array(256);
for (let i = 0; i < 256; i += 1) {
  const c = i / 255;
  toLin[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
const SRGB_STEPS = 4096;
const toSrgb = new Uint8ClampedArray(SRGB_STEPS + 1);
for (let i = 0; i <= SRGB_STEPS; i += 1) {
  const c = i / SRGB_STEPS;
  toSrgb[i] = Math.round(255 * (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055));
}
const enc = (lin) => toSrgb[Math.max(0, Math.min(SRGB_STEPS, Math.round(lin * SRGB_STEPS)))];
const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
const hexLin = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [toLin[(n >> 16) & 255], toLin[(n >> 8) & 255], toLin[n & 255]];
};
/* highlights roll off above 0.88 instead of clipping */
const soft = (v) => (v <= 0.88 ? v : 0.88 + 0.12 * (1 - Math.exp(-(v - 0.88) / 0.12)));

const images = new Map();
function load(src) {
  if (!images.has(src)) {
    images.set(src, new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`recolor: could not load ${src}`));
      img.src = src;
    }));
  }
  return images.get(src);
}

function pixels(img, w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  return { canvas, ctx, data: ctx.getImageData(0, 0, w, h) };
}

/* weight 0–1 of how much a ghost pixel is garment: its alpha, less the
   bright, near-neutral FIGS label and zip pulls */
function ghostWeight(r, g, b, a) {
  if (!a) return 0;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const v = max / 255;
  const s = max ? (max - min) / max : 0;
  const bright = Math.min(1, Math.max(0, (v - 0.5) / 0.25));
  const neutral = 1 - Math.min(1, Math.max(0, (s - 0.12) / 0.13));
  return (a / 255) * (1 - bright * neutral);
}

async function render(src, hex, maskSrc) {
  const [img, maskImg] = await Promise.all([load(src), maskSrc ? load(maskSrc) : null]);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const { canvas, ctx, data } = pixels(img, w, h);
  const px = data.data;
  const mask = maskImg ? pixels(maskImg, w, h).data.data : null;
  const n = w * h;
  const weight = new Float32Array(n);
  /* pass 1: garment weights and the garment's mean luminance */
  let sum = 0;
  let wsum = 0;
  for (let i = 0, p = 0; i < n; i += 1, p += 4) {
    const wgt = mask ? mask[p] / 255 : ghostWeight(px[p], px[p + 1], px[p + 2], px[p + 3]);
    weight[i] = wgt;
    if (wgt > 0.5) {
      sum += lum(toLin[px[p]], toLin[px[p + 1]], toLin[px[p + 2]]) * wgt;
      wsum += wgt;
    }
  }
  const ref = wsum ? sum / wsum : 0.03;
  const [tr, tg, tb] = hexLin(hex);
  const tl = lum(tr, tg, tb);
  /* a lighter target flattens navy's deep shading; a darker one keeps it */
  const gamma = Math.max(0.34, Math.min(1, 1 - 0.3 * Math.log(Math.max(tl, 1e-4) / ref)));
  /* pass 2: the target colour at each pixel's relative brightness */
  for (let i = 0, p = 0; i < n; i += 1, p += 4) {
    const wgt = weight[i];
    if (wgt <= 0.002) continue;
    const r = toLin[px[p]]; const g = toLin[px[p + 1]]; const b = toLin[px[p + 2]];
    const shade = (lum(r, g, b) / ref) ** gamma;
    const nr = soft(tr * shade); const ng = soft(tg * shade); const nb = soft(tb * shade);
    px[p] = enc(r + (nr - r) * wgt);
    px[p + 1] = enc(g + (ng - g) * wgt);
    px[p + 2] = enc(b + (nb - b) * wgt);
  }
  ctx.putImageData(data, 0, 0);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  return URL.createObjectURL(blob);
}

const results = new Map();
/* `source` is the colour the image was shot in: asking for it returns the
   file itself. Concurrent asks for the same image and colour share a job. */
export function recolor(src, color, { mask = null, source = 'navy' } = {}) {
  if (!color || color.slug === source) return Promise.resolve(src);
  const key = `${src}|${color.hex}`;
  if (!results.has(key)) results.set(key, render(src, color.hex, mask).catch((e) => { console.warn(e); return src; }));
  return results.get(key);
}

/* warm the cache for a colour without blocking the frame that asked */
export function prewarm(jobs) {
  let i = 0;
  const next = () => {
    if (i >= jobs.length) return;
    const [src, color, opts] = jobs[i++];
    recolor(src, color, opts).then(() => setTimeout(next, 40));
  };
  next();
}
